import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, Image, ScrollView, ActivityIndicator,
} from 'react-native';
import Moment from 'moment';
import Modal from 'react-native-modal';
import Style from '../../../constants/Style';
import Colors from '../../../constants/Colors';
import CloseModalIcon from '../../../components/CloseModalIcon';
import PressableStyled from '../../../components/PressableStyled';
import CustomTextField from '../../../components/CustomTextField';
import Button from '../../../components/Button';
import { DateRange } from '../../../redux/dateRangeSlice';
import DropdownMenu from '../../../components/DropdownMenu';
import CheckBoxItem from '../../../components/CheckBoxItem';
import {
  ImagePreview, imagesToPdf, PageOrientation, PageSize, SolveAnalyticsWidget,
} from '../Utils';

type DataSelection = {
  name: string,
}

const dataSelectionOptions: DataSelection[] = Object.values(SolveAnalyticsWidget)
  .map((name: SolveAnalyticsWidget) => ({ name }));

const pageSizeOptions: DataSelection[] = Object.values(PageSize)
  .map((name: PageSize) => ({ name }));

const orientationOptions: DataSelection[] = Object.values(PageOrientation)
  .map((name: PageOrientation) => ({ name }));

interface ExportModalProps {
  isVisible: boolean,
  hide: () => void,
  range: DateRange,
  previewImages: ImagePreview[],
  preFilterPreviewImages: ImagePreview[],
  filterSelectionDisabled: boolean,
  previewLoading: boolean,
  templateGroupName?: string,
  templateName?: string,
  userGroupName?: string,
}

const PreviewImage = ({ uri, name }: ImagePreview) => {
  const [aspectRatio, setAspectRatio] = useState<number>(3);

  Image.getSize(uri, (width: number, height: number) => setAspectRatio(width / height));

  return <>
    <Text style={styles.title}>{name}</Text>
    <Image
      source={{ uri }}
      key={`preview_${name}`}
      style={[styles.image, { aspectRatio }]}
    />
  </>;
};

const ExportModal = ({
  isVisible, hide, range, previewImages, preFilterPreviewImages, filterSelectionDisabled,
  previewLoading, templateGroupName, templateName, userGroupName,
}: ExportModalProps) => {
  const startDate: Date = new Date(range.start);
  const endDate: Date = new Date(range.end);
  const pageTitle: string = `Solve analytics: ${Moment(startDate).format('DD MMM YYYY')} to ${Moment(endDate).format('DD MMM YYYY')}`;
  const defaultName: string = `Solve_Analytics_${startDate.getFullYear()}_${startDate.getMonth() + 1}_${startDate.getDate()}-${endDate.getFullYear()}_${endDate.getMonth() + 1}_${endDate.getDate()}.pdf`;
  const [fileName, setFileName] = useState<string>(defaultName);
  const [dataSelectionDropdownVisible, setDataSelectionDropdownVisible] = useState<boolean>(false);
  const [dataSelection, setDataSelection] = useState<DataSelection[]>(dataSelectionOptions);
  const [filtersSelected, setFiltersSelected] = useState<boolean>(true);
  const [sizeSelectionDropdownVisible, setSizeSelectionDropdownVisible] = useState<boolean>(false);
  const [orientationSelectionDropdownVisible,
    setOrientationSelectionDropdownVisible] = useState<boolean>(false);
  const [selectedPageSize, setSelectedPageSize] = useState<DataSelection>(pageSizeOptions[0]);
  const [selectedOrientation,
    setSelectedOrientation] = useState<DataSelection>(orientationOptions[0]);

  const disabled: boolean = !(dataSelection.length > 0);

  const pageSubtitle: string = `Template group: ${templateGroupName || 'All'}\nTemplate: ${templateName || 'All'}\nUser group: ${userGroupName || 'All'}`;

  useEffect(() => {
    if (!dataSelectionDropdownVisible) return;
    setSizeSelectionDropdownVisible(false);
    setOrientationSelectionDropdownVisible(false);
  }, [dataSelectionDropdownVisible]);

  useEffect(() => {
    if (!sizeSelectionDropdownVisible) return;
    setDataSelectionDropdownVisible(false);
    setOrientationSelectionDropdownVisible(false);
  }, [sizeSelectionDropdownVisible]);

  useEffect(() => {
    if (!orientationSelectionDropdownVisible) return;
    setSizeSelectionDropdownVisible(false);
    setDataSelectionDropdownVisible(false);
  }, [orientationSelectionDropdownVisible]);

  useEffect(() => {
    setSizeSelectionDropdownVisible(false);
    setDataSelectionDropdownVisible(false);
    setOrientationSelectionDropdownVisible(false);
  }, [filtersSelected]);

  const onModalHide: () => void = () => {
    setFileName(defaultName);
    setDataSelectionDropdownVisible(false);
    setDataSelection(dataSelectionOptions);
    setFiltersSelected(true);
    setSizeSelectionDropdownVisible(false);
    setOrientationSelectionDropdownVisible(false);
    setSelectedPageSize(pageSizeOptions[0]);
    setSelectedOrientation(orientationOptions[0]);
  };

  const generatePdfData: () => Promise<string> = async () => {
    const selectedPreviewNames: string[] = dataSelection.map((item: DataSelection) => item.name);
    const blob: Blob = await imagesToPdf(
      previewImages?.filter((preview: ImagePreview) => selectedPreviewNames.includes(preview.name)),
      selectedPageSize.name as PageSize,
      selectedOrientation.name as PageOrientation,
      pageTitle,
      pageSubtitle,
    );
    const url: string = URL.createObjectURL(blob);
    return url;
  };

  const download: () => void = async () => {
    const url: string = await generatePdfData();

    // eslint-disable-next-line no-undef
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(url);
  };

  const view: () => void = async () => {
    const url: string = await generatePdfData();
    // eslint-disable-next-line no-undef
    const newTab: Window = window.open(url, '_blank');
    newTab.document.title = fileName;

    URL.revokeObjectURL(url);
  };

  return <Modal
    {...{ isVisible, onModalHide }}
    onBackButtonPress={hide}
    onBackdropPress={hide}
    style={Style.modal}
  >
    <View style={[Style.modalContent, styles.container]}>
      <ScrollView
        style={styles.previewSection}
        contentContainerStyle={!previewLoading && styles.previewContent}
      >
        {previewLoading ? <ActivityIndicator
          size='large'
          style={styles.loadingIndicator}
        /> : <>
          <Text style={styles.pageTitle}>{pageTitle}</Text>
          {(filtersSelected ? previewImages : preFilterPreviewImages)
            ?.map(({ uri, name }: ImagePreview) => (dataSelection.some((item) => item.name === name)
              && <PreviewImage {...{ uri, name }} />))}
        </>}
      </ScrollView>
      <View style={styles.formSection}>
        <View style={styles.formContent}>
          <Text style={[Style.title, styles.title]}>
            {'Export preview'}
          </Text>
          <CustomTextField
            title='Name'
            placeholder='Enter name'
            value={fileName}
            updateValue={setFileName}
          />
          <DropdownMenu
            expanded={dataSelectionDropdownVisible}
            setExpanded={setDataSelectionDropdownVisible}
            title='data'
            options={dataSelectionOptions}
            selectedOption={dataSelection}
            setSelectedOption={setDataSelection}
            label='Select data'
            defaultTitle={dataSelection.length === dataSelectionOptions.length ? 'All data'
              : 'Custom'}
            firstOptionTitle='All data'
            style={[styles.dropdown, { zIndex: 4 }]}
            multiSelect
          />
          {false && <CheckBoxItem
            title='Include filters'
            toggled={filtersSelected}
            toggle={setFiltersSelected}
            style={styles.checkBox}
            disabled={filterSelectionDisabled}
          />}
          <DropdownMenu
            expanded={sizeSelectionDropdownVisible}
            setExpanded={setSizeSelectionDropdownVisible}
            label='Size'
            options={pageSizeOptions}
            selectedOption={selectedPageSize}
            setSelectedOption={setSelectedPageSize}
            style={[styles.dropdown, { zIndex: 3 }]}
            hideSelectAll
            radioButtonMode={!(pageSizeOptions?.length > 3)}
          />
          <DropdownMenu
            expanded={orientationSelectionDropdownVisible}
            setExpanded={setOrientationSelectionDropdownVisible}
            label='Orientation'
            options={orientationOptions}
            selectedOption={selectedOrientation}
            setSelectedOption={setSelectedOrientation}
            style={styles.dropdown}
            hideSelectAll
            radioButtonMode={!(orientationOptions?.length > 3)}
          />
        </View>
        <View style={Style.row}>
          <Button
            title='Cancel'
            onPress={hide}
          />
          <View style={Style.row}>
            <Button
              title='View in new tab'
              onPress={view}
              {...{ disabled }}
            />
            <Button
              affirmative
              title='Download as PDF'
              onPress={download}
              {...{ disabled }}
            />
          </View>
        </View>
        <PressableStyled
          style={Style.closeButton}
          onPress={hide}
        >
          <CloseModalIcon />
        </PressableStyled>
      </View>
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    width: 1072,
    height: 768,
    alignSelf: 'center',
    borderRadius: 10,
    overflow: 'hidden',
  },
  previewSection: {
    flex: 1,
    backgroundColor: Colors.light.hereworksWhite500,
    paddingVertical: 40,
    paddingHorizontal: 64,
  },
  previewContent: {
    padding: 24,
    backgroundColor: '#FFF',
  },
  image: {
    alignSelf: 'stretch',
    backgroundColor: '#FFF',
    marginBottom: 24,
  },
  formSection: {
    flex: 1,
    padding: 24,
    justifyContent: 'space-between',
  },
  formContent: {
    flex: 1,
  },
  title: {
    textAlign: 'left',
    marginBottom: 24,
  },
  dropdown: {
    marginTop: 16,
    zIndex: 2,
  },
  checkBox: {
    marginVertical: 8,
  },
  pageTitle: {
    fontSize: 16,
    lineHeight: 24,
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue500,
    marginBottom: 4,
  },
  loadingIndicator: {
    marginTop: '50%',
  },
});

export default ExportModal;
